// src/hooks/useAddressAutocomplete.js
//
// Wraps Places API (New) - Autocomplete + Place Details - as plain REST calls
// (no JS SDK), matching the house style used elsewhere in the app (Postcodes.io,
// Resend, Turnstile are all plain fetch calls). Session tokens group an
// autocomplete "session" (keystrokes + the final details fetch) so Google bills
// it as one session instead of per-request.

import { useState, useRef, useCallback } from 'react';

const PLACES_API_KEY = process.env.REACT_APP_GOOGLE_PLACES_API_KEY;
const DEBOUNCE_MS = 300;
const MIN_INPUT_LENGTH = 3;

function newSessionToken() {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function getComponent(components, type) {
  return components.find((c) => c.types?.includes(type))?.longText;
}

function parsePlaceDetails(data) {
  const components = data.addressComponents || [];
  const postcode = getComponent(components, 'postal_code') || '';

  const parts = [];
  const streetNumber = getComponent(components, 'street_number');
  const route = getComponent(components, 'route');
  if (streetNumber && route) parts.push(`${streetNumber} ${route}`);
  else if (route) parts.push(route);

  const sublocality = getComponent(components, 'sublocality');
  if (sublocality) parts.push(sublocality);

  // UK addresses often carry "postal_town" (e.g. London) rather than "locality"
  const locality = getComponent(components, 'locality') || getComponent(components, 'postal_town');
  if (locality) parts.push(locality);

  const address = parts.length > 0 ? parts.join(', ') : (data.formattedAddress || '');

  return {
    address,
    postcode,
    latitude: data.location?.latitude ?? null,
    longitude: data.location?.longitude ?? null,
  };
}

export default function useAddressAutocomplete() {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const sessionTokenRef = useRef(newSessionToken());
  const debounceRef = useRef(null);

  const clearSuggestions = useCallback(() => setSuggestions([]), []);

  const search = useCallback((input) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!PLACES_API_KEY || !input || input.trim().length < MIN_INPUT_LENGTH) {
      setSuggestions([]);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const resp = await fetch('https://places.googleapis.com/v1/places:autocomplete', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Goog-Api-Key': PLACES_API_KEY,
          },
          body: JSON.stringify({
            input,
            sessionToken: sessionTokenRef.current,
            includedRegionCodes: ['gb'],
          }),
        });
        const data = await resp.json();
        setSuggestions(data.suggestions || []);
      } catch (err) {
        console.warn('⚠️ Places autocomplete failed:', err);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);
  }, []);

  const selectPlace = useCallback(async (placeId) => {
    if (!PLACES_API_KEY) return null;
    try {
      const resp = await fetch(
        `https://places.googleapis.com/v1/places/${placeId}?sessionToken=${sessionTokenRef.current}`,
        {
          headers: {
            'X-Goog-Api-Key': PLACES_API_KEY,
            'X-Goog-FieldMask': 'addressComponents,formattedAddress,location',
          },
        },
      );
      const data = await resp.json();
      return parsePlaceDetails(data);
    } catch (err) {
      console.warn('⚠️ Place details lookup failed:', err);
      return null;
    } finally {
      // Session is over once Details has been fetched - start a fresh token
      // for the next autocomplete session.
      sessionTokenRef.current = newSessionToken();
    }
  }, []);

  return { suggestions, loading, search, selectPlace, clearSuggestions };
}
